import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  forwardRef,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
interface CapabilityInputField {
  name: string;
  type: string;
  required: boolean;
  nullable: boolean;
  maxLength?: number | null;
  values?: string[];
  items?: CapabilityInputField[];
  maxItems?: number | null;
  recordTypeCode?: string | null;
}
function isParameterObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** Edits the registered payload shape, without inventing command inputs or coercing retained values. */
@Component({
  selector: 'phms-typed-capability-inputs, phms-schedule-parameters',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    UiButtonDirective,
    UiControlDirective,
    forwardRef(() => TypedCapabilityInputsComponent),
  ],
  template: `
    <div *ngFor="let field of fields; trackBy: fieldKey" class="parameter">
      <label class="parameter-title"
        >{{ field.name }}
        <span>{{ field.required ? '(required)' : '(optional)' }}</span></label
      >
      <label *ngIf="!field.required"
        ><input
          type="checkbox"
          [checked]="has(field)"
          [disabled]="disabled"
          (change)="toggle(field, $any($event.target).checked)"
        />
        Supply this parameter</label
      >
      <ng-container *ngIf="has(field)">
        <label *ngIf="field.nullable"
          ><input
            type="checkbox"
            [checked]="values[field.name] === null"
            [disabled]="disabled"
            (change)="
              set(
                field,
                $any($event.target).checked ? null : defaultValue(field)
              )
            "
          />
          Use null</label
        >
        <ng-container *ngIf="values[field.name] !== null">
          <select
            *ngIf="field.values?.length; else typed"
            uiControl
            [attr.aria-label]="field.name"
            [ngModel]="values[field.name]"
            (ngModelChange)="set(field, $event)"
            [ngModelOptions]="standalone"
            [disabled]="disabled"
          >
            <option value="">Choose a value</option>
            <option *ngFor="let value of field.values" [value]="value">
              {{ value }}
            </option>
          </select>
          <ng-template #typed
            ><ng-container [ngSwitch]="field.type">
              <input
                *ngSwitchCase="'string'"
                uiControl
                type="text"
                [attr.aria-label]="field.name"
                [attr.maxlength]="field.maxLength"
                [ngModel]="values[field.name]"
                (ngModelChange)="set(field, $event)"
                [ngModelOptions]="standalone"
                [disabled]="disabled"
              />
              <input
                *ngSwitchCase="'integer'"
                uiControl
                type="number"
                step="1"
                [attr.aria-label]="field.name"
                [ngModel]="values[field.name]"
                (ngModelChange)="set(field, $event === null ? '' : $event)"
                [ngModelOptions]="standalone"
                [disabled]="disabled"
              />
              <input
                *ngSwitchCase="'number'"
                uiControl
                type="number"
                step="any"
                [attr.aria-label]="field.name"
                [ngModel]="values[field.name]"
                (ngModelChange)="set(field, $event === null ? '' : $event)"
                [ngModelOptions]="standalone"
                [disabled]="disabled"
              />
              <select
                *ngSwitchCase="'boolean'"
                uiControl
                [attr.aria-label]="field.name"
                [ngModel]="values[field.name]"
                (ngModelChange)="set(field, $event)"
                [ngModelOptions]="standalone"
                [disabled]="disabled"
              >
                <option [ngValue]="true">Yes</option>
                <option [ngValue]="false">No</option>
              </select>
              <div *ngSwitchCase="'array'">
                <p *ngIf="!isArray(values[field.name])">
                  The retained value is not a typed row list. Replace it
                  explicitly to repair this parameter.
                </p>
                <div
                  *ngFor="
                    let row of rows(field);
                    let index = index;
                    trackBy: rowKey
                  "
                  class="parameter-row"
                >
                  <span>Row {{ index + 1 }}</span>
                  <phms-typed-capability-inputs
                    [fields]="field.items || emptyFields"
                    [values]="record(row)"
                    [disabled]="disabled"
                    (valuesChange)="setRow(field, index, $event)"
                  />
                  <button
                    uiButton
                    type="button"
                    [disabled]="disabled"
                    (click)="removeRow(field, index)"
                  >
                    Remove row
                  </button>
                </div>
                <button
                  uiButton
                  type="button"
                  [disabled]="
                    disabled || rows(field).length >= (field.maxItems || 0)
                  "
                  (click)="addRow(field)"
                >
                  Add {{ field.name }} row
                </button>
              </div>
              <p *ngSwitchDefault>
                Unsupported retained parameter type: {{ field.type }}. Its value
                is preserved.
              </p>
            </ng-container></ng-template
          >
        </ng-container>
      </ng-container>
    </div>
    <section *ngIf="unknownNames.length">
      <p>
        Parameters not declared by this operation are preserved. Remove them
        explicitly before publication.
      </p>
      <div *ngFor="let name of unknownNames">
        <code>{{ name }}: {{ values[name] | json }}</code>
        <button
          uiButton
          type="button"
          [disabled]="disabled"
          (click)="remove(name)"
        >
          Remove {{ name }}
        </button>
      </div>
    </section>
  `,
  styles: [
    `
      .parameter {
        display: grid;
        gap: 0.35rem;
        margin-block: 0.6rem;
      }
      .parameter-title {
        font-weight: 600;
      }
      .parameter-title span {
        font-weight: 400;
      }
      .parameter-row {
        border-inline-start: 2px solid var(--custom-border-color);
        padding: 0.5rem;
      }
      input:not([type='checkbox']),
      select {
        width: 100%;
      }
    `,
  ],
})
export class TypedCapabilityInputsComponent {
  @Input() fields: readonly CapabilityInputField[] = [];
  @Input() values: Record<string, unknown> = {};
  @Input() disabled = false;
  @Output() valuesChange = new EventEmitter<Record<string, unknown>>();
  readonly standalone = { standalone: true };
  readonly emptyFields: CapabilityInputField[] = [];
  private readonly emptyRows: unknown[] = [];
  get unknownNames(): string[] {
    return Object.keys(this.values).filter(
      (name) => !this.fields.some((field) => field.name === name),
    );
  }
  fieldKey(_index: number, field: CapabilityInputField): string {
    return field.name;
  }
  rowKey(index: number): number {
    return index;
  }
  has(field: CapabilityInputField): boolean {
    return Object.hasOwn(this.values, field.name) || field.required;
  }
  isArray(value: unknown): boolean {
    return Array.isArray(value);
  }
  rows(field: CapabilityInputField): unknown[] {
    const value = this.values[field.name];
    return Array.isArray(value) ? value : this.emptyRows;
  }
  record(value: unknown): Record<string, unknown> {
    return isParameterObject(value) ? value : {};
  }
  defaultValue(field: CapabilityInputField): unknown {
    return field.type === 'array' ? [] : field.type === 'boolean' ? false : '';
  }
  toggle(field: CapabilityInputField, supplied: boolean): void {
    if (supplied) this.set(field, this.defaultValue(field));
    else this.remove(field.name);
  }
  set(field: CapabilityInputField, value: unknown): void {
    if (!this.disabled)
      this.valuesChange.emit({ ...this.values, [field.name]: value });
  }
  remove(name: string): void {
    if (this.disabled) return;
    const next = { ...this.values };
    delete next[name];
    this.valuesChange.emit(next);
  }
  addRow(field: CapabilityInputField): void {
    if (this.rows(field).length < (field.maxItems ?? 0))
      this.set(field, [...this.rows(field), {}]);
  }
  removeRow(field: CapabilityInputField, index: number): void {
    this.set(
      field,
      this.rows(field).filter((_row, i) => i !== index),
    );
  }
  setRow(
    field: CapabilityInputField,
    index: number,
    value: Record<string, unknown>,
  ): void {
    this.set(
      field,
      this.rows(field).map((row, i) => (i === index ? value : row)),
    );
  }
}
